class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  initializeMinicartSlickSlider() {
    const swiperElement = document.querySelector('.minicart-slider');
    if (swiperElement && !swiperElement.swiper) {
      const swiper = new Swiper('.minicart-slider', {
        slidesPerView: 'auto',
        spaceBetween: 12,
        loop: false,
      });

      const syncAtStartClass = () => {
        swiperElement.classList.toggle('is-at-start', swiper.activeIndex === 0);
      };

      syncAtStartClass();
      swiper.on('slideChange', syncAtStartClass);
      swiper.on('transitionEnd', syncAtStartClass);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setupMinicartUpsellButtons() {
    const upsellButtons = document.querySelectorAll('[data-minicart-upsell-add]');
    
    upsellButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = e.currentTarget.dataset.variantId;
        
        if (!variantId) return;

        const originalText = button.querySelector('span').textContent;
        button.disabled = true;
        button.querySelector('span').textContent = 'Adicionando...';

        try {
          const sections = this.getSectionsToRender().map((section) => section.id);
          
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              sections: sections.join(','),
              sections_url: window.location.pathname
            })
          });

          if (response.ok) {
            const data = await response.json();
            
            // Refresh cart drawer
            this.renderContents(data);
            
            // Publish cart update event
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-drawer-upsell',
                cartData: data
              });
            }
          }
        } catch (error) {
          console.error('Error adding upsell to cart:', error);
        } finally {
          button.disabled = false;
          button.querySelector('span').textContent = originalText;
        }
      });
    });
  }

  open(triggeredBy) {
    this.initializeMinicartSlickSlider();
    this.setupMinicartUpsellButtons();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Remove is-empty class from both drawer__inner and cart-drawer
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    if (this.classList.contains('is-empty')) {
      this.classList.remove('is-empty');
    }
    
    this.productId = parsedState.id;

    const existingMinicartSlider = document.querySelector('.minicart-slider');
    if (existingMinicartSlider && existingMinicartSlider.swiper && typeof existingMinicartSlider.swiper.destroy === 'function') {
      existingMinicartSlider.swiper.destroy(true, true);
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.setupMinicartUpsellButtons();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html || '', 'text/html');
    const element = parsed.querySelector(selector);
    if (element) return element.innerHTML;
    return parsed.body ? parsed.body.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
